import { create } from 'zustand';
import { Organization } from '../../../../types/organization.types';

interface FubAccordionState {
  // State
  openStep: number;
  currentOrgId: string | null;

  // Derived values (computed from organization data)
  isConfigured: (org?: Organization) => boolean;
  hasCustomFields: (org?: Organization) => boolean;
  hasStagesAllowlist: (org?: Organization) => boolean;
  hasTeamMembersSync: (org?: Organization) => boolean;
  hasWebhooksSetup: (org?: Organization) => boolean;
  hasSubdomain: (org?: Organization) => boolean;
  completedSteps: (org?: Organization) => Record<number, boolean>;

  // Actions
  setOpenStep: (step: number) => void;
  toggleStep: (step: number, currentOpenStep: number) => void;
  completeStep: (step: number) => void;
  resetForNewOrg: (org?: Organization) => void;
}

export const useFubAccordionStore = create<FubAccordionState>((set, get) => ({
  // Initial state
  openStep: 1,
  currentOrgId: null,

  // Derived values
  isConfigured: (org?: Organization) => Boolean(org?.crmApiKeyHash),
  hasCustomFields: (org?: Organization) => Boolean(org?.fubSetup?.customfields?.length),
  hasStagesAllowlist: (org?: Organization) => Boolean(org?.fubSetup?.allowedStages?.length),
  hasTeamMembersSync: (org?: Organization) => Boolean(org?.fubSetup?.teamMembers),
  hasWebhooksSetup: (org?: Organization) => Boolean(org?.fubSetup?.webHooks),
  hasSubdomain: (org?: Organization) => Boolean(org?.fubSetup?.subdomain),

  completedSteps: (org?: Organization) => {
    const isConfigured = get().isConfigured(org);
    const hasCustomFields = get().hasCustomFields(org);
    const hasStagesAllowlist = get().hasStagesAllowlist(org);
    const hasTeamMembersSync = get().hasTeamMembersSync(org);
    const hasWebhooksSetup = get().hasWebhooksSetup(org);
    const hasSubdomain = get().hasSubdomain(org);

    return {
      1: isConfigured,
      2: hasCustomFields,
      3: hasStagesAllowlist,
      4: hasTeamMembersSync,
      5: hasWebhooksSetup,
      6: hasSubdomain,
    };
  },

  // Actions
  setOpenStep: (step: number) => set({ openStep: step }),

  toggleStep: (step: number, currentOpenStep: number) => {
    set({ openStep: currentOpenStep === step ? 0 : step });
  },

  completeStep: (step: number) => {
    // Open the next step if available
    if (step < 6) {
      set({ openStep: step + 1 });
    }
  },

  resetForNewOrg: (org?: Organization) => {
    // If no org provided, do nothing
    if (!org) return;

    // Get current state
    const currentOrgId = get().currentOrgId;

    // Only update if the org ID has changed
    if (org.id !== currentOrgId) {
      // Store the new org ID
      set({ currentOrgId: org.id });

      // Determine initial step based on org configuration
      const isConfigured = get().isConfigured(org);
      const hasCustomFields = get().hasCustomFields(org);
      const hasStagesAllowlist = get().hasStagesAllowlist(org);
      const hasTeamMembersSync = get().hasTeamMembersSync(org);
      const hasWebhooksSetup = get().hasWebhooksSetup(org);
      const hasSubdomain = get().hasSubdomain(org);

      // Determine initial step based on configuration
      let initialStep = 1;
      if (hasSubdomain) {
        initialStep = 0; // All steps completed
      } else if (hasWebhooksSetup) {
        initialStep = 6; // Subdomain setup
      } else if (hasTeamMembersSync) {
        initialStep = 5; // Webhooks setup
      } else if (hasStagesAllowlist) {
        initialStep = 4; // Team members sync
      } else if (hasCustomFields) {
        initialStep = 3; // Stages configuration
      } else if (isConfigured) {
        initialStep = 2; // Custom fields setup
      }

      // Reset the open step
      set({ openStep: initialStep });
    }
  },
}));
